/**
 * Hesap ekranı: profil özeti, parola değiştirme, 2FA yönetimi ve sunucu tanılaması.
 *
 * ## Neden bu ekran var?
 *
 * Gerçek sunucuda parola değiştirmenin hiçbir yolu yoktu: ne web uçlu ne
 * CLI. İlk kurulumdan sonra yönetici parolası kilitlenmişti. Bu ekran
 * modern iki sütunlu dashboard tasarımıyla kullanıcı profili, parola güncelleme,
 * 2FA (TOTP) yönetimi ve sistem/donanım hızlandırma yeteneklerini sunar.
 */
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { ApiError, api, type Capabilities } from '../api.ts';
import { CatalogPreferencesCard } from '../components/CatalogPreferencesCard.tsx';
import { HomePreferencesCard } from '../components/HomePreferencesCard.tsx';
import { LibraryScanList } from '../components/LibraryScanList.tsx';
import { ErrorNotice, Loading } from '../components/States.tsx';
import {
  AlertIcon,
  ArrowLeftIcon,
  BulbIcon,
  CheckIcon,
  ClipboardIcon,
  CrownIcon,
  LinkIcon,
  LockIcon,
  MonitorIcon,
  ShieldIcon,
  UserIcon,
} from '../Icon.tsx';
import { clearOfflineCatalog } from '../offlineCatalog.ts';
import { qrDataUrl } from '../qr.ts';
import { useSession } from '../session.tsx';
import type { TvModeApi, TvModePreference } from '../tvMode.ts';
import { clientKindLabel, clientVersionLabel, useClientVersion } from '../useClientVersion.ts';
import { formatBytes, useDiskStatus } from '../useDiskStatus.ts';

/** Sunucunun parola gücü kuralıyla aynı (auth/service.ts). */
const MIN_LENGTH = 10;

export function AccountScreen({
  capabilities,
  tvMode,
  onBack,
}: {
  capabilities?: Capabilities | null;
  /** TV modu tercihi; verilmezse kart gösterilmez (eski çağrılar). */
  tvMode?: TvModeApi | undefined;
  onBack: () => void;
}): ReactNode {
  const { user } = useSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const clientVersion = useClientVersion();

  if (!user) return <Loading label="Hesap yükleniyor…" />;

  /**
   * İstemci doğrulaması.
   *
   * Sunucu zaten reddediyor; buradaki kontrol kullanıcıyı gidiş-dönüş
   * yapmadan uyarmak için. Kural kopyası yanlışsa kullanıcı sunucuya gidip
   * öğrenir — bu yüzden MIN_LENGTH sunucudaki validatePassword ile birlikte
   * değiştirilmelidir.
   */
  const weakness = describeWeakness(newPassword);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (weakness !== null) {
      setError(weakness);
      return;
    }
    if (newPassword !== repeat) {
      setError('Yeni parola iki kez aynı girilmeli.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Yeni parola eskisiyle aynı olamaz.');
      return;
    }

    setSaving(true);
    try {
      const result = await api.changePassword(currentPassword, newPassword);
      if (user !== null) await clearOfflineCatalog(user.id);
      setCurrentPassword('');
      setNewPassword('');
      setRepeat('');
      setNotice(
        result.revokedSessions > 0
          ? `Parola başarıyla değiştirildi. Diğer cihazlardaki ${result.revokedSessions} oturum güvenlik amacıyla kapatıldı.`
          : 'Parola başarıyla değiştirildi.',
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Parola değiştirilemedi. Bağlantıyı denetle.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="account">
      {/* 1. Üst Profil ve Karşılama Başlığı */}
      <div className="account__hero">
        <div className="account__profile">
          <div className="account__avatar" aria-hidden="true">
            {(user.displayName || user.username).charAt(0).toUpperCase()}
          </div>
          <div className="account__identity">
            <div className="account__identity-main">
              <h1 className="account__name">{user.displayName || user.username}</h1>
              <span className="account__role-badge">
                {user.role === 'admin' ? (
                  <>
                    <CrownIcon size={13} />
                    Yönetici
                  </>
                ) : (
                  <>
                    <UserIcon size={13} />
                    Kullanıcı
                  </>
                )}
              </span>
              {user.has2fa ? (
                <span className="account__badge account__badge--2fa-on">
                  <ShieldIcon size={13} />
                  2FA Korumalı
                </span>
              ) : (
                <span className="account__badge account__badge--2fa-off">
                  <AlertIcon size={13} />
                  2FA Kapalı
                </span>
              )}
            </div>
            <p className="account__sub">
              Kullanıcı adı: <code>@{user.username}</code> · Hesap ve güvenlik ayarları
            </p>
          </div>
        </div>

        <div className="account__hero-actions">
          <AccountAdminLink role={user.role} />
          <button type="button" className="button button--ghost account__back-btn" onClick={onBack}>
            <ArrowLeftIcon size={16} />
            Ana Sayfaya Dön
          </button>
        </div>
      </div>

      {/* 2. İki Sütunlu Modern Grid (Masaüstü) / Tek Sütun (Mobil) */}
      <div className="account__grid">
        {/* Sol Kolon: Ana sayfa, güvenlik ve parola */}
        <div className="account__col account__col--main">
          <HomePreferencesCard />
          <CatalogPreferencesCard />

          {/* Parola Değiştirme Kartı */}
          <section className="account__card">
            <div className="account__card-header">
              <div className="account__card-icon">
                <LockIcon size={20} />
              </div>
              <div>
                <h2 className="account__card-title">Parola Değiştir</h2>
                <p className="account__card-desc">
                  Hesap güvenliğinizi korumak için güçlü ve benzersiz bir parola belirleyin.
                </p>
              </div>
            </div>

            <div className="account__callout">
              <span className="account__callout-icon">
                <BulbIcon size={18} />
              </span>
              <p className="account__callout-text">
                Parolanızı değiştirdiğinizde <strong>bu cihazdaki oturumunuz açık kalır</strong>,
                diğer tüm cihazlardaki oturumlar güvenlik gereği otomatik kapatılır.
              </p>
            </div>

            <form className="account__form" onSubmit={(e) => void submit(e)}>
              <label className="field">
                <span className="field__label">Mevcut Parola</span>
                <input
                  type="password"
                  className="account__input"
                  placeholder="Mevcut parolanız"
                  value={currentPassword}
                  autoComplete="current-password"
                  required
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </label>

              <label className="field">
                <span className="field__label">Yeni Parola</span>
                <input
                  type="password"
                  className="account__input"
                  placeholder="En az 10 karakter"
                  value={newPassword}
                  autoComplete="new-password"
                  required
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <div className="account__password-rules">
                  <span className={newPassword.length >= MIN_LENGTH ? 'rule--pass' : 'rule--hint'}>
                    {newPassword.length >= MIN_LENGTH ? <CheckIcon size={13} /> : '•'} En az{' '}
                    {MIN_LENGTH} karakter
                  </span>
                  <span
                    className={
                      weakness === null && newPassword.length > 0 ? 'rule--pass' : 'rule--hint'
                    }
                  >
                    {weakness === null && newPassword.length > 0 ? <CheckIcon size={13} /> : '•'}{' '}
                    İki farklı karakter sınıfı (harf, rakam, simge)
                  </span>
                </div>
              </label>

              <label className="field">
                <span className="field__label">Yeni Parola (Tekrar)</span>
                <input
                  type="password"
                  className="account__input"
                  placeholder="Yeni parolanızı doğrulayın"
                  value={repeat}
                  autoComplete="new-password"
                  required
                  onChange={(e) => setRepeat(e.target.value)}
                />
                {newPassword.length > 0 && repeat.length > 0 && newPassword !== repeat && (
                  <small className="field__warn">Parolalar birbiriyle eşleşmiyor.</small>
                )}
              </label>

              {error !== null && <ErrorNotice message={error} />}
              {notice !== null && (
                <div className="account__notice">
                  <CheckIcon size={14} /> {notice}
                </div>
              )}

              <div className="account__form-actions">
                <button
                  type="submit"
                  className="button button--primary account__submit-btn"
                  disabled={saving}
                >
                  {saving ? 'Kaydediliyor…' : 'Parolayı Güncelle'}
                </button>
              </div>
            </form>
          </section>

          {/* İki Faktörlü Doğrulama (2FA) */}
          <TwoFactorSection />
        </div>

        {/* Sağ Kolon: Sistem Bilgisi & Oturum Özeti */}
        <div className="account__col account__col--side">
          {/* Oturum Durumu Kartı */}
          <section className="account__card account__card--compact">
            <div className="account__card-header">
              <div className="account__card-icon">
                <ShieldIcon size={20} />
              </div>
              <div>
                <h3 className="account__card-title">Hesap Güvenliği</h3>
                <p className="account__card-desc">Mevcut oturum ve güvenlik özeti</p>
              </div>
            </div>

            <div className="account__spec-list">
              <div className="account__spec-item">
                <span className="account__spec-label">Yetki / Rol</span>
                <span className="account__spec-val">
                  {user.role === 'admin' ? 'Yönetici (Full Erişim)' : 'Standart Kullanıcı'}
                </span>
              </div>
              <div className="account__spec-item">
                <span className="account__spec-label">2FA Doğrulama</span>
                <span className="account__spec-val">
                  {user.has2fa ? (
                    <span className="text-success">
                      <CheckIcon size={13} /> Korunuyor
                    </span>
                  ) : (
                    <span className="text-warn">Kapalı (Önerilir)</span>
                  )}
                </span>
              </div>
              <div className="account__spec-item">
                <span className="account__spec-label">Aktif İstemci</span>
                <span className="account__spec-val">{clientKindLabel(clientVersion)}</span>
              </div>
              <div className="account__spec-item">
                <span className="account__spec-label">İstemci Sürümü</span>
                <span className="account__spec-badge">{clientVersionLabel(clientVersion)}</span>
              </div>
            </div>
          </section>
          {/* Sunucu ve Sistem Bilgisi Kartı */}
          {capabilities && (
            <section className="account__card account__card--compact">
              <div className="account__card-header">
                <div className="account__card-icon">
                  <MonitorIcon size={20} />
                </div>
                <div>
                  <h3 className="account__card-title">Sunucu ve Sistem</h3>
                  <p className="account__card-desc">
                    Çalışan medya sunucusunun donanım ve akış profili
                  </p>
                </div>
              </div>

              <div className="account__spec-list">
                <div className="account__spec-item">
                  <span className="account__spec-label">Sunucu Sürümü</span>
                  <span className="account__spec-badge">v{capabilities.serverVersion}</span>
                </div>
                <div className="account__spec-item">
                  <span className="account__spec-label">Tasarım Sistemi</span>
                  <a className="link" href="#/tasarim">
                    Kataloğu aç
                  </a>
                </div>
                <div className="account__spec-item">
                  <span className="account__spec-label">HEVC Donanım Hızlandırma</span>
                  <span className="account__spec-val">
                    {capabilities.transcode.hevcHardwareEncode ? (
                      <span className="text-success">
                        <CheckIcon size={13} /> Donanım Destekli
                      </span>
                    ) : (
                      'Yok (H.264 dönüştürme)'
                    )}
                  </span>
                </div>
                <div className="account__spec-item">
                  <span className="account__spec-label">FFmpeg Dönüştürme</span>
                  <span className="account__spec-val">
                    {capabilities.transcode.available ? (
                      <span className="text-success">Açık (Transcode Aktif)</span>
                    ) : (
                      'Kapalı'
                    )}
                  </span>
                </div>
                <div className="account__spec-item">
                  <span className="account__spec-label">İzleme Rapor Sıklığı</span>
                  <span className="account__spec-val">
                    {Math.round(capabilities.progress.reportIntervalMs / 1000)} saniyede bir
                  </span>
                </div>
                <div className="account__spec-item">
                  <span className="account__spec-label">İzlendi Eşiği</span>
                  <span className="account__spec-val">
                    %{Math.round(capabilities.progress.watchedThresholdRatio * 100)}
                  </span>
                </div>
              </div>
            </section>
          )}
          <TvModeSection tvMode={tvMode} />
          {/* Kütüphane Taramaları - "Son Tarama" bilgisinin yeni yeri. */}
          <LibraryScanList />
          <DiskUsageCard />{' '}
        </div>
      </div>
    </div>
  );
}

/**
 * Disk kullanımı kartı (ADR-014).
 *
 * Kullanıcının sorusu nettir: "**sunucumda ne kadar alan kaldı?**" Cevap
 * üç sayıdır: dosya sisteminin kalan alanı, transcode önbelleğinin ve uzak
 * disk önbelleğinin kullandığı yer. Eşikleri sunucu belirler; burada yalnız
 * ölçümler gösterilir.
 */
function DiskUsageCard(): ReactNode {
  const { value: status, updatedAt } = useDiskStatus();

  if (status === null) return null;

  const filesystem = status.filesystem;
  const remote = status.remoteCache;

  return (
    <section className="account__card account__card--compact">
      <div className="account__card-header">
        <div className="account__card-icon">
          <MonitorIcon size={20} />
        </div>
        <div>
          <h3 className="account__card-title">Disk Kullanımı</h3>
          <p className="account__card-desc">Sunucuda kalan alan ve önbelleklerin payı</p>
        </div>
      </div>

      <div className="account__spec-list">
        <div className="account__spec-item">
          <span className="account__spec-label">Kalan Alan</span>
          <span className="account__spec-val">
            {filesystem === null ? (
              'Ölçülemedi'
            ) : (
              <>
                <strong>{formatBytes(filesystem.freeBytes)}</strong> /{' '}
                {formatBytes(filesystem.totalBytes)}
                {' · '}%{Math.round(filesystem.usedRatio * 100)} kullanılıyor
              </>
            )}
          </span>
        </div>

        <div className="account__spec-item">
          <span className="account__spec-label">Transcode Önbelleği</span>
          <span className="account__spec-val">{formatBytes(status.transcode.usedBytes)}</span>
        </div>

        {remote !== null && (
          <div className="account__spec-item">
            <span className="account__spec-label">Uzak Disk Önbelleği</span>
            <span className="account__spec-val">
              {formatBytes(remote.usedBytes)}
              {remote.limitBytes !== null && ` / ${formatBytes(remote.limitBytes)}`}
              {remote.ratio !== null && ` · %${Math.round(remote.ratio * 100)}`}
            </span>
          </div>
        )}

        {status.messages.length > 0 && (
          <div className="account__spec-item">
            <span className="account__spec-label">Uyarı</span>
            <span className="account__spec-val">{status.messages.join(' ')}</span>
          </div>
        )}

        {updatedAt !== null && (
          <div className="account__spec-item">
            <span className="account__spec-label">Son Ölçüm</span>
            <span className="account__spec-val">
              {new Date(updatedAt).toLocaleTimeString('tr-TR')}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function AccountAdminLink({ role }: { role: string }): ReactNode {
  if (role !== 'admin') return null;
  return (
    <a className="button button--ghost" href="#/yonetim">
      <ShieldIcon size={16} />
      Yönetim Merkezi
    </a>
  );
}

function TvModeSection({ tvMode }: { tvMode?: TvModeApi | undefined }): ReactNode {
  if (tvMode === undefined) return null;

  const options: Array<{ value: TvModePreference; label: string }> = [
    { value: 'auto', label: 'Otomatik' },
    { value: 'on', label: 'Açık' },
    { value: 'off', label: 'Kapalı' },
  ];

  return (
    <section className="account__card account__card--compact">
      <div className="account__card-header">
        <div className="account__card-icon">
          <MonitorIcon size={20} />
        </div>
        <div>
          <h2 className="account__card-title">TV Modu</h2>
          <p className="account__card-desc">Uzaktan kumanda ve 10-foot odak</p>
        </div>
      </div>

      <p className="account__hint">
        TV modu geniş ekranlarda kartları büyütür, odak halkasını kalınlaştırır ve ok tuşlarıyla
        gezinmeyi açar. Geniş bir masaüstü monitörü tek başına TV sayılmaz.
      </p>

      <fieldset className="account__segmented" aria-label="TV modu tercihi">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="account__segmented-btn"
            aria-pressed={tvMode.preference === option.value}
            onClick={() => tvMode.setPreference(option.value)}
          >
            {option.label}
          </button>
        ))}
      </fieldset>

      <p className="account__hint" role="status" aria-live="polite">
        Durum: <strong>{tvMode.enabled ? 'Etkin' : 'Kapalı'}</strong>
        {tvMode.detected ? ' · TV tarayıcısı algılandı' : ''}
      </p>
    </section>
  );
}

function TwoFactorSection(): ReactNode {
  const { user, refreshUser } = useSession();
  const [setupData, setSetupData] = useState<{ secret: string; uri: string } | null>(null);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!user) return null;

  const handleStartSetup = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const data = await api.setup2fa();
      setSetupData(data);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '2FA kurulumu başlatılamadı.');
    } finally {
      setBusy(false);
    }
  };

  const handleEnableSuccess = async () => {
    setSetupData(null);
    setNotice(
      'İki faktörlü doğrulama başarıyla etkinleştirildi! Artık giriş yaparken 6 haneli kod gerekecektir.',
    );
    await refreshUser();
  };

  const handleDisableSuccess = async () => {
    setShowDisableForm(false);
    setNotice('İki faktörlü doğrulama devre dışı bırakıldı.');
    await refreshUser();
  };

  return (
    <section className="account__card">
      <div className="account__card-header">
        <div className="account__card-icon">
          <ShieldIcon size={20} />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <h2 className="account__card-title">İki Faktörlü Doğrulama (2FA)</h2>
            {user.has2fa ? (
              <span className="account__badge account__badge--2fa-on">
                <CheckIcon size={12} /> Etkin
              </span>
            ) : (
              <span className="account__badge account__badge--2fa-off">Kapalı</span>
            )}
          </div>
          <p className="account__card-desc">
            Google Authenticator, Authy veya Apple Parolalar ile 6 haneli kodlar üreterek hesabınızı
            yetkisiz erişimlere karşı koruyun.
          </p>
        </div>
      </div>

      {error !== null && <ErrorNotice message={error} />}
      {notice !== null && (
        <div className="account__notice">
          <CheckIcon size={14} /> {notice}
        </div>
      )}

      {!user.has2fa ? (
        <div className="account__2fa-body">
          {!setupData ? (
            <button
              type="button"
              className="button button--primary"
              onClick={() => void handleStartSetup()}
              disabled={busy}
            >
              {busy ? (
                'Hazırlanıyor…'
              ) : (
                <>
                  <ShieldIcon size={15} />
                  İki Faktörlü Doğrulamayı Etkinleştir
                </>
              )}
            </button>
          ) : (
            <TwoFactorSetupBox
              setupData={setupData}
              onCancel={() => setSetupData(null)}
              onError={setError}
              onSuccess={() => void handleEnableSuccess()}
            />
          )}
        </div>
      ) : (
        <div className="account__2fa-body">
          {!showDisableForm ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(46, 213, 115, 0.08)',
                borderRadius: '10px',
                border: '1px solid rgba(46, 213, 115, 0.2)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.88rem',
                  color: '#2ed573',
                  fontWeight: 600,
                }}
              >
                <ShieldIcon size={16} />
                Hesabınız iki faktörlü kimlik doğrulama ile güvende.
              </div>
              <button
                type="button"
                className="button button--ghost"
                style={{
                  color: 'var(--danger)',
                  borderColor: 'rgba(255, 107, 107, 0.3)',
                  padding: '6px 12px',
                  fontSize: '0.82rem',
                }}
                onClick={() => setShowDisableForm(true)}
              >
                2FA'yı Devre Dışı Bırak…
              </button>
            </div>
          ) : (
            <TwoFactorDisableForm
              onCancel={() => setShowDisableForm(false)}
              onError={setError}
              onSuccess={() => void handleDisableSuccess()}
            />
          )}
        </div>
      )}
    </section>
  );
}

function TwoFactorSetupBox({
  setupData,
  onCancel,
  onError,
  onSuccess,
}: {
  setupData: { secret: string; uri: string };
  onCancel: () => void;
  onError: (msg: string | null) => void;
  onSuccess: () => void;
}): ReactNode {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [revealSecret, setRevealSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState(false);

  // Gizli anahtar taşıyan otpauth URI'si dış servise gönderilmez; QR yerel
  // olarak (tarayıcıda) üretilir.
  useEffect(() => {
    let cancelled = false;
    setQrUrl(null);
    setQrError(false);
    void qrDataUrl(setupData.uri).then(
      (url) => {
        if (!cancelled) setQrUrl(url);
      },
      () => {
        if (!cancelled) setQrError(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [setupData.uri]);

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Pano erişilemezse sessizce geç
    }
  };

  const handleEnable = async (event: FormEvent) => {
    event.preventDefault();
    onError(null);
    setBusy(true);
    try {
      await api.enable2fa(code.trim());
      onSuccess();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Doğrulama kodu geçersiz.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="account__2fa-setup">
      <div>
        <h3 style={{ margin: '0 0 6px', fontSize: '1.02rem', color: '#fff', fontWeight: 700 }}>
          1. Authenticator Uygulamasında Tanımlayın
        </h3>
        <p style={{ fontSize: '0.86rem', color: 'var(--text-muted)', margin: 0 }}>
          Google Authenticator, Microsoft Authenticator, Authy veya Apple Parolalar ile karekodu
          tarayın:
        </p>
      </div>

      <div className="account__2fa-qr-row">
        <div className="account__2fa-qr-frame">
          {qrUrl !== null ? (
            <img
              src={qrUrl}
              alt="2FA QR Kodu"
              width={150}
              height={150}
              style={{ display: 'block' }}
            />
          ) : (
            <p className="account__2fa-qr-hint" role="status">
              {qrError
                ? 'Karekod üretilemedi; aşağıdaki gizli anahtarı kullanın.'
                : 'Karekod hazırlanıyor…'}
            </p>
          )}
        </div>

        <div style={{ flex: 1, minWidth: '240px' }}>
          <span
            style={{
              fontSize: '0.78rem',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 600,
            }}
          >
            Karekod tarayamıyorsanız gizli anahtar:
          </span>
          <div className="account__2fa-secret-box">
            <code className="account__2fa-code-badge">
              {revealSecret ? setupData.secret : '•••• •••• •••• ••••'}
            </code>
            <button
              type="button"
              className="button button--ghost"
              style={{ padding: '6px 14px', fontSize: '0.84rem' }}
              onClick={() => setRevealSecret((current) => !current)}
              aria-pressed={revealSecret}
            >
              {revealSecret ? 'Gizle' : 'Göster'}
            </button>
            <button
              type="button"
              className="button button--ghost"
              style={{ padding: '6px 14px', fontSize: '0.84rem' }}
              onClick={() => void handleCopySecret()}
              disabled={!revealSecret}
              title={revealSecret ? undefined : 'Önce gizli anahtarı gösterin'}
            >
              {copied ? (
                <>
                  <CheckIcon size={14} />
                  Kopyalandı
                </>
              ) : (
                <>
                  <ClipboardIcon size={14} />
                  Kopyala
                </>
              )}
            </button>
          </div>
          <div style={{ marginTop: '10px' }}>
            <a
              href={setupData.uri}
              className="button button--ghost"
              style={{ fontSize: '0.8rem', padding: '5px 10px', textDecoration: 'none' }}
            >
              <LinkIcon size={13} />
              Doğrudan Uygulamada Aç
            </a>
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '16px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '1.02rem', color: '#fff', fontWeight: 700 }}>
          2. Oluşturulan 6 Haneli Kodu Girin
        </h3>
        <form onSubmit={(e) => void handleEnable(e)} className="account__2fa-verify-form">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            required
            className="account__2fa-otp-input"
          />
          <button
            type="submit"
            className="button button--primary"
            style={{ padding: '11px 20px', borderRadius: '10px' }}
            disabled={busy || code.length < 6}
          >
            {busy ? 'Doğrulanıyor…' : 'Onayla ve 2FA’yı Aç'}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={onCancel}
            style={{ borderRadius: '10px' }}
          >
            Vazgeç
          </button>
        </form>
      </div>
    </div>
  );
}

function TwoFactorDisableForm({
  onCancel,
  onError,
  onSuccess,
}: {
  onCancel: () => void;
  onError: (msg: string | null) => void;
  onSuccess: () => void;
}): ReactNode {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const handleDisable = async (event: FormEvent) => {
    event.preventDefault();
    onError(null);
    setBusy(true);
    try {
      await api.disable2fa(password);
      onSuccess();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Parola doğrulanamadı.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void handleDisable(e)}
      style={{
        background: 'rgba(255, 107, 107, 0.06)',
        border: '1px solid rgba(255, 107, 107, 0.25)',
        borderRadius: '14px',
        padding: '20px',
        maxWidth: '460px',
      }}
    >
      <h4 style={{ margin: '0 0 8px', color: 'var(--danger)', fontSize: '1rem', fontWeight: 700 }}>
        İki Faktörlü Doğrulamayı Devre Dışı Bırak
      </h4>
      <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        Güvenliğiniz için bu işlemi tamamlamadan önce lütfen hesap parolanızı girin:
      </p>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Mevcut parolanız"
        required
        className="account__input"
        style={{ marginBottom: '14px' }}
      />
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          type="submit"
          className="button"
          style={{
            background: 'var(--danger)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
          }}
          disabled={busy || password.length === 0}
        >
          {busy ? 'Kapatılıyor…' : "Evet, 2FA'yı Kapat"}
        </button>
        <button
          type="button"
          className="button button--ghost"
          onClick={onCancel}
          style={{ borderRadius: '8px' }}
        >
          Vazgeç
        </button>
      </div>
    </form>
  );
}

/**
 * Yeni parolanın gücünü denetler (sunucuyla aynı kural).
 *
 * Ayrı fonksiyon: hem formun canlı geri bildirimi hem testler kullanıyor.
 * `null` = sorun yok.
 */
export function describeWeakness(password: string): string | null {
  if (password.length === 0) return null; // Boşsa "henüz yazmadı", hata değil.
  if (password.length < MIN_LENGTH) return `Parola en az ${MIN_LENGTH} karakter olmalı.`;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) =>
    pattern.test(password),
  ).length;
  if (classes < 2) return 'Parola en az iki farklı karakter sınıfı içermeli.';
  return null;
}
